// Значение множителя на символе Wild Multiplier.
//
// Значение рисует не игра, а сам символ. Его скелет держит по слоту на каждое число и стрелку
// на циферблате, а анимация win_1…win_10 ставит их в позу своего значения с первого же кадра,
// см. GDD, раздел 5.2. Поэтому помеченная ячейка показывает скелет, застывший на этом кадре,
// а не картинку с числом поверх неё.
//
// Значение приходит меткой на ячейке поля, а держат ячейки барабаны. Поэтому символ каждый
// кадр берёт положение у барабанов: на сдвиге он едет вниз вместе со своей меткой,
// см. Nudge Respin в GDD, раздел 4.3.
//
// Свою анимацию выигрыша ячейка играет тем же скелетом, только уже на ходу, см. ui/winShow.ts.
// Там символ снимается с барабанов, и здесь он снимается вместе с ним.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container } from 'pixi.js';

import type { SymbolId } from '../../api/gameConfig';
import { markedWinAnimation } from '../../assets/catalog';
import type { Spine } from '../../assets/manager';
import type { SymbolLibrary } from '../../game/symbols';
import type { MarkedCell } from '../../ui/reels';
import { poseSpine } from '../../ui/scene';

/** Что символам нужно от барабанов: где сейчас стоят помеченные ячейки. */
export interface MarkedField {
  markedCells(): MarkedCell[];
}

/** Символы Wild Multiplier поверх поля: каждый показывает своё значение. */
export class MultiplierSymbols {
  readonly view: Container = new Container();

  readonly #field: MarkedField;
  readonly #symbols: SymbolLibrary;
  readonly #symbol: SymbolId;
  readonly #shown: Spine[] = [];

  /** Значение, в позе которого стоит каждый символ. Ноль — поза ещё не задана. */
  readonly #values: number[] = [];

  /** @param symbol Идентификатор символа Wild Multiplier. Его скелет и рисует значение. */
  constructor(symbols: SymbolLibrary, field: MarkedField, symbol: SymbolId) {
    this.#symbols = symbols;
    this.#field = field;
    this.#symbol = symbol;
  }

  /**
   * Ставит символы туда, где сейчас стоят помеченные ячейки.
   *
   * Ячейка, снятая под анимацию выигрыша, символа не получает: её скелет уже играет свою
   * анимацию в показе выигрыша, и второй такой же лёг бы поверх него. Барабаны об этом знают,
   * поэтому снятую ячейку они отдают невидимой.
   */
  update(): void {
    const cells: MarkedCell[] = this.#field.markedCells();

    cells.forEach((cell: MarkedCell, index: number) => {
      const shown: Spine = this.#shown[index] ?? this.#add();

      // Поза задаётся только на смене значения: анимация иначе начиналась бы заново каждый кадр.
      if (this.#values[index] !== cell.value) {
        this.#values[index] = cell.value;
        poseSpine(shown, markedWinAnimation(cell.value));
      }

      shown.position.set(cell.x, cell.y);
      shown.visible = cell.visible;
    });

    // Меток стало меньше: лишние символы уходят с экрана, но остаются в запасе.
    for (let extra = cells.length; extra < this.#shown.length; extra++)
      this.#shown[extra].visible = false;
  }

  /** Заводит ещё один символ в запас. Пул растёт по мере надобности и живёт со сценой. */
  #add(): Spine {
    // Скелет символа нарисован вокруг своего начала координат, поэтому он встаёт прямо
    // в середину ячейки, как и статичная картинка под ним.
    const shown: Spine = this.#symbols.spine(this.#symbol);

    this.#shown.push(shown);
    this.#values.push(0);
    this.view.addChild(shown);

    return shown;
  }
}
